"use client";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone, type FileRejection } from "react-dropzone";
import { Upload, X } from "lucide-react";
import { extractText } from "@/utils/extractResume";
import { countWords, MAX_JOB_DESCRIPTION_WORDS } from "@/utils/text";
import { ROUTES } from "@/config/routes";
import {
  RESUME_CACHE_KEY,
  setCache,
  type PendingResume,
} from "@/utils/cacheConsts";

interface ResumeUploaderProps {
  maxWords?: number;
}

function ResumeUploader({ maxWords = MAX_JOB_DESCRIPTION_WORDS }: ResumeUploaderProps) {
  const navigate = useNavigate();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [jobDescription, setJobDescription] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // Allowed mime types for react-dropzone accept prop
  const accept: Record<string, string[]> = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
  };

  // Helper: validate by extension fallback (some browsers may report generic mime)
  const isAllowedByName = (file: File) => {
    const name = file.name.toLowerCase();
    return name.endsWith(".pdf") || name.endsWith(".docx");
  };

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    // If dropzone rejected the file(s), show first rejection reason
    if (fileRejections && fileRejections.length > 0) {
      setUploadedFile(null);
      setFileError("Invalid file. Only PDF or DOCX allowed.");
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    // double-check extension in case of odd mime
    if (!isAllowedByName(file)) {
      setUploadedFile(null);
      setFileError("Only PDF or DOCX files are allowed.");
      return;
    }

    // ok
    setUploadedFile(file);
    setFileError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    maxFiles: 1,
  });

  const removeFile = () => {
    setUploadedFile(null);
    setFileError(null);
  };

  // Extract the resume text now and hand it to the dashboard via a short-lived
  // cache, so generation can continue right after sign-in.
  const handleGenerate = async () => {
    if (!uploadedFile || !jobDescription.trim() || fileError) return;

    setLoading(true);
    try {
      const extractedLocalStorageText = await extractText(uploadedFile);
      setCache<PendingResume>(
        RESUME_CACHE_KEY,
        { extractedLocalStorageText, localStorageJobDescription: jobDescription },
        30 * 1000
      );
      navigate(ROUTES.AUTH);
    } catch (err) {
      console.error("Resume text extraction failed:", err);
      setFileError("Couldn't read this file. Please try a different PDF or DOCX.");
    } finally {
      setLoading(false);
    }
  };

  const wordCount = countWords(jobDescription);

  const canGenerate = useMemo(() => {
    return (
      !!uploadedFile &&
      !fileError &&
      jobDescription.trim().length > 0 &&
      wordCount <= maxWords
    );
  }, [uploadedFile, fileError, jobDescription, wordCount, maxWords]);

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      {/* Resume Dropzone Card */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div
          {...getRootProps()}
          className={`flex items-center gap-4 border-2 border-dashed rounded-xl p-4 cursor-pointer transition
            ${
              isDragActive
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          aria-label="Upload resume (PDF or DOCX)"
        >
          <input {...getInputProps()} />
          <div className="flex-shrink-0">
            <Upload className="h-10 w-10 text-blue-600" />
          </div>

          <div className="flex-1">
            {!uploadedFile ? (
              <>
                <p className="font-medium text-gray-800">
                  Drag & drop your resume here or click to browse
                </p>
                <p className="text-sm text-gray-500">
                  Accepted: PDF, DOCX (single file)
                </p>
              </>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-800 break-words">
                    {uploadedFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatBytes(uploadedFile.size)}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile();
                  }}
                  className="ml-4 inline-flex items-center justify-center rounded-full p-2 hover:bg-gray-100"
                  aria-label="Remove uploaded file"
                >
                  <X className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* File error */}
        {fileError && <p className="mt-3 text-sm text-red-600">{fileError}</p>}
      </div>

      {/* Job Description Card */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <label
          htmlFor="jobDesc"
          className="block text-sm font-medium text-gray-800 mb-2"
        >
          Job Description
        </label>

        <textarea
          id="jobDesc"
          rows={6}
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          placeholder={`Paste the job description here (max ${maxWords} words)`}
          className="w-full rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 p-4 text-gray-700 resize-none"
        />

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-gray-500">Limit: {maxWords} words</p>
          <p className="text-xs text-gray-500">
            {wordCount} / {maxWords}
          </p>
        </div>

        {/* Action Button */}
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!canGenerate}
            aria-disabled={!canGenerate}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-150 ${
              canGenerate
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:from-blue-700 hover:to-purple-700"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <>
                <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Generating...
              </>
            ) : (
              "Generate Tailored Resume"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
export default ResumeUploader;
